import { tWithKeyAttr as t } from "../i18nHelper";
import {
  IAppointmentProgram,
  IDatedText,
  INotification,
  ITimeRange,
  TAddUserRequest,
  TLabeledItem,
  TPrescriptionForm,
} from "../types/types";
import chatIcon from "../images/messages_icon_blue.svg";
import calendarIcon from "../images/calendar_icon.svg";
import reportsIcon from "../images/reports_icon.svg";
import questionnaireReportsIcon from "../images/questionnaire_reports_icon.svg";
import treatmentPlanIcon from "../images/treatment_plan_shortcut_icon.svg";
import { IContactUsTheme } from "./categories";
import { StylesConfig } from "react-select";
import {
  ISpecializationArea,
  TQuestionnaireEntry,
  TQuestionnaireQuestion,
  TQuestionOption,
  TRRMListEntry,
  TSupportMessage,
} from "../services/types/user.types";
import { number } from "yup";

export const BASE_URL =
  window.location.hostname.includes("test") ||
  window.location.hostname.includes("local")
    ? "http://localhost:7241/"
    : "http://localhost:7241/";
export const testUserEmail = "-";
export const testUserEmail2 = "-";

export const mobileWidth = "(max-width: 600px)";
export const tabletWidth = "(max-width:1200px)";

export const phonePrefixOptions = [
  "050",
  "051",
  "052",
  "053",
  "054",
  "055",
  "057",
  "058",
];
export const therapistPOCQuestionnaireName = "שאלון POC טרפיסט";
export const customerService = "customerService";

// Accepts:
// - E.164 international: + followed by 8 to 15 digits (e.g., +972501234567)
// - Digits-only international for Israel starting with 972 (8–9 more digits)
// - Israeli local mobile: 05XXXXXXXX (10 digits)
export const phoneRegExp = /^(\+\d{8,15}|972\d{8,9}|05\d{8})$/;

export enum appointmentOptions {
  Cancel = "cancel",
  Update = "update",
  Create = "create",
}

/** Locale keys for `Date.getDay()` order: Sunday (0) … Saturday (6). */
export const weekDayTitleKeys = [
  "DAY_SUNDAY",
  "DAY_MONDAY",
  "DAY_TUESDAY",
  "DAY_WEDNESDAY",
  "DAY_THURSDAY",
  "DAY_FRIDAY",
  "DAY_SATURDAY",
] as const;
export const rangesExample: ITimeRange[] = [
  {
    start: "8:00",
    end: "12:30",
    statusId: 1,
  },
  {
    start: "13:30",
    end: "15:30",
    statusId: 1,
  },
];
export const weekdays: {
  [key: number | string]: { value: string; titleKey: string };
} = {
  1: { value: "sunday", titleKey: "DAY_SUNDAY" },
  2: { value: "monday", titleKey: "DAY_MONDAY" },
  3: { value: "tuesday", titleKey: "DAY_TUESDAY" },
  4: { value: "wednesday", titleKey: "DAY_WEDNESDAY" },
  5: { value: "thursday", titleKey: "DAY_THURSDAY" },
  6: { value: "friday", titleKey: "DAY_FRIDAY" },
  7: { value: "saturday", titleKey: "DAY_SATURDAY" },
};

export const hoursRange: string[] = [
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23",
  "00",
];
export const minutesRange: string[] = ["00", "10", "20", "30", "40", "50"];

export const isTimeSmaller = (h1: string, h2: string): boolean => {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  return toMinutes(h1) < toMinutes(h2);
};
export const isTimeBiggerOrEqual = (h1: string, h2: string): boolean => {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  return toMinutes(h1) > toMinutes(h2) || toMinutes(h1) === toMinutes(h2);
};
export const isTimeEqual = (h1: string, h2: string): boolean => {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };
  return toMinutes(h1) === toMinutes(h2);
};

export const increment = (
  array: string[],
  currentValue: string,
  type: string
) => {
  const [hours, minutes] = currentValue.split(":");
  if (type === "h") {
    const index = array.indexOf(hours);
    const nextValue = array[(index + 1) % array.length];
    return `${nextValue}:${minutes}`;
  } else if (type === "m") {
    const index = array.indexOf(minutes ?? "00");
    const nextValue = array[(index + 1) % array.length];
    return `${hours}:${nextValue}`;
  }
  const index = array.indexOf(currentValue);
  return array[(index - 1 + array.length) % array.length];
};
export const decrement = (
  array: string[],
  currentValue: string,
  type: string
) => {
  const [hours, minutes] = currentValue.split(":");
  if (type === "h") {
    const index = array.indexOf(hours);
    const nextValue = array[(index - 1 + array.length) % array.length];
    return `${nextValue}:${minutes}`;
  } else if (type === "m") {
    const index = array.indexOf(minutes ?? "00");
    const nextValue = array[(index - 1 + array.length) % array.length];
    return `${hours}:${nextValue}`;
  }
  const index = array.indexOf(currentValue);
  return array[(index - 1 + array.length) % array.length];
};

export const newTimeRange = {
  start: `${hoursRange[3]}:${minutesRange[0]}`,
  end: `${hoursRange[10]}:${minutesRange[0]}`,
  statusId: 1,
};
export const notificationsExample: INotification[] = [
  { unread: true, text: "פנייה חדשה מד״ר נאמן", section: "chat" },
  { unread: true, text: "הופקה עבורך חשבונית חדשה", section: "profile" },
  { unread: true, text: "תגובה לשרשור שפתחת בפורום", section: "chat" },
  { unread: false, text: "פנייה חדשה מנחום, המדריך", section: "chat" },
  { unread: false, text: "התקבל מרשם חדש", section: "treatment" },
];

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);
export const getMainPageTexts = (): IDatedText[] => [
  {
    id: 1,
    date: today,
    text: "MAIN_PAGE_UPDATE_1_TEXT",
  },
  {
    id: 2,
    date: today,
    text: "MAIN_PAGE_UPDATE_2_TEXT",
  },
  {
    id: 3,
    date: today,
    text: "MAIN_PAGE_UPDATE_3_TEXT",
    link: "https://drive.google.com/drive/folders/1uGHf6X4MH5y-2QaJtE70SiJ4Rd4_2SLG?usp=drive_link",
    linkText: t("MAIN_PAGE_UPDATE_3_LINK_TEXT"),
  },
  {
    id: 4,
    date: yesterday,
    text: "MAIN_PAGE_UPDATE_4_TEXT",
  },
  {
    id: 5,
    date: yesterday,
    text: "MAIN_PAGE_UPDATE_5_TEXT",
  },
  {
    id: 6,
    date: yesterday,
    text: "MAIN_PAGE_UPDATE_6_TEXT",
  },
];

export interface ICategory {
  value: string;
  title: string;
  img: string;
}
export const therapistLinks: ICategory[] = [
  { value: "therapistsQuestionsReports", img: questionnaireReportsIcon, title: "THERAPISTS_QUESTIONS_REPORTS_TAB" },
  // { value: "forum", img: forumIcon, title: "FORUM" },
  {
    value: "calendar",
    img: calendarIcon,
    title: "CALENDAR",
  },
  {
    value: "treatmentPlan",
    img: treatmentPlanIcon,
    title: "MY_TREATMENT_PLANS",
  },
  { value: "pendingReports", img: reportsIcon, title: "UNREPORTED_MEETINGS" },
  { value: "messages", img: chatIcon, title: "CHAT" },

];

export const filterOptions = [
  { value: "status", label: "ADD_TREATMENT_PLAN_LABEL_STATUS" },
  { value: "urgent", label: "FILTER_OPTION_URGENT" },
  { value: "today", label: "FILTER_OPTION_TODAY" },
  { value: "week", label: "FILTER_OPTION_WEEK" },
  { value: "month", label: "FILTER_OPTION_MONTH" },
  { value: "date", label: "FILTER_OPTION_SPECIFIC_DATE" },
];

export const filterOptionsMain = [
  { value: "rrm", label: "FILTER_OPTION_RRM" },
  { value: "today", label: "FILTER_OPTION_TODAY" },
  { value: "week", label: "FILTER_OPTION_WEEK" },
  { value: "month", label: "FILTER_OPTION_MONTH" },
  { value: "cycle", label: "MAIN_PAGE_INQUIRY_CYCLE" },
  { value: "specificQuestion", label: "FILTER_OPTION_SPECIFIC_QUESTION" },
];

export const filterOptionsTreatmentPlan = [
  { value: "today", label: "FILTER_OPTION_TODAY" },
  { value: "week", label: "FILTER_OPTION_WEEK" },
  { value: "month", label: "FILTER_OPTION_MONTH" },
];

export const filterOptionsMainWithStatus = [
  ...filterOptionsMain,
  { value: "status", label: "ADD_TREATMENT_PLAN_LABEL_STATUS" },
];

export interface IOption {
  value: string;
  label: string;
  desc?: string;
  QuestionnaireCompaniesId?: number;
  isDisabled?: boolean;
}
export type TOptionWithDesc = IOption & {
  desc: string;
};

export interface INumberOption extends Omit<IOption, "value"> {
  value: number;
}

export const reasonVariants: IOption[] = [
  { value: "1", label: "שיחת תמיכה" },
  { value: "2", label: "בדידות" },
  { value: "3", label: "דימוי עצמי" },
  { value: "4", label: "זהות" },
  { value: "5", label: "תגובה לאבדו" },
  { value: "6", label: "זוגיות" },
  { value: "7", label: "קשיים רגשיים" },
  { value: "8", label: "מתחים ומריבות" },
  { value: "9", label: "יחסי הורים-ילדים" },
  { value: "10", label: "אלימות מינית" },
  { value: "11", label: "אלימות פיזית" },
  { value: "12", label: "מיניות" },
  { value: "13", label: "פרידה" },
  { value: "14", label: "לימודים" },
  { value: "15", label: "תעסוקה" },
  { value: "16", label: "צבא" },
  { value: "17", label: "מצב כלכלי" },
  { value: "18", label: "מצב ביטחוני" },
  { value: "19", label: "חרדה" },
  { value: "20", label: "דיכאון" },
  { value: "21", label: "כאב נפשי" },
  { value: "22", label: "טראומה" },
  { value: "23", label: "הפרעות אכילה" },
  { value: "24", label: "התמכרויות" },
  { value: "25", label: "סכיזופרניה" },
  { value: "26", label: "השלכות נפשיות של נכויות" },
];

export const rrmVariants = [
  {
    label: "יש סיכון לאובדנות. יש לדרג:",
    value: "1",
    options: [
      {
        value: "יש סיכון לאובדנות:נמוך",
        label: "נמוך",
        desc: "יש סיכון לאובדנות:",
      },
      {
        value: "יש סיכון לאובדנות:בינוני",
        label: t("DOCUMENT_URGENCY_MEDIUM"),
        desc: "יש סיכון לאובדנות:",
      },
      {
        value: "יש סיכון לאובדנות:גבוה",
        label: "גבוה",
        desc: "יש סיכון לאובדנות:",
      },
    ],
  },
  {
    label: "אין  סיכון לאובדנות",
    value: "אין  סיכון לאובדנות",
  },
  {
    label: "יש לבצע הערכה מקצועית. יש לדרג דחיפות:",
    options: [
      {
        value: "יש לבצע הערכה מקצועית. דחיפות:נמוך",
        label: "נמוך",
        desc: "יש לבצע הערכה מקצועית. דחיפות:",
      },
      {
        value: "יש לבצע הערכה מקצועית. דחיפות:בינוני",
        label: t("DOCUMENT_URGENCY_MEDIUM"),
        desc: "יש לבצע הערכה מקצועית. דחיפות:",
      },
      {
        value: "יש לבצע הערכה מקצועית. דחיפות:גבוה",
        label: "גבוה",
        desc: "יש לבצע הערכה מקצועית. דחיפות:",
      },
    ],
  },
];

export const recommendations = [
  {
    label: "לתאם פגישת הערכה עם:",
    options: [
      {
        value: "פסיכיאטר/ית",
        label: "פסיכיאטר/ית",
        desc: "לתאם פגישת הערכה עם: ",
      },
      {
        value: "פסיכולוג/ית קליני מומחה",
        label: "פסיכולוג/ית קליני מומחה",
        desc: "לתאם פגישת הערכה עם: ",
      },
      {
        value: "פסיכולוג/ית התפתחותי מומחה",
        label: "פסיכולוג/ית התפתחותי מומחה",
        desc: "לתאם פגישת הערכה עם: ",
      },
      {
        value: "פסיכולוג/ית שיקומי מומחה",
        label: "פסיכולוג/ית שיקומי מומחה",
        desc: "לתאם פגישת הערכה עם: ",
      },
      {
        value: "פסיכולוג/ית חינוכי מומחה",
        label: "פסיכולוג/ית חינוכי מומחה",
        desc: "לתאם פגישת הערכה עם: ",
      },
    ],
  },
  {
    label: "לתאם פגישת טיפול עם:",
    options: [
      {
        value: "פסיכיאטר/ית",
        label: "פסיכיאטר/ית",
        desc: "לתאם פגישת טיפול עם: ",
      },
      {
        value: "פסיכולוג/ית קליני מומחה",
        label: "פסיכולוג/ית קליני מומחה",
        desc: "לתאם פגישת טיפול עם: ",
      },
      {
        value: "פסיכולוג/ית התפתחותי מומחה",
        label: "פסיכולוג/ית התפתחותי מומחה",
        desc: "לתאם פגישת טיפול עם: ",
      },
      {
        value: "פסיכולוג/ית שיקומי מומחה",
        label: "פסיכולוג/ית שיקומי מומחה",
        desc: "לתאם פגישת טיפול עם: ",
      },
      {
        value: "תומך/ת טיפול",
        label: "תומך/ת טיפול",
        desc: "לתאם פגישת טיפול עם: ",
      },
    ],
  },
  {
    label: "סטינג פגישות מומלץ:",
    options: [
      {
        value: "1 לשבוע",
        label: "1 לשבוע",
        desc: "סטינג פגישות מומלץ: ",
      },
      {
        value: "2 לשבוע",
        label: "2 לשבוע",
        desc: "סטינג פגישות מומלץ: ",
      },
      {
        value: "3 לשבוע",
        label: "3 לשבוע",
        desc: "סטינג פגישות מומלץ: ",
      },
      {
        value: "1 לחודש",
        label: "1 לחודש",
        desc: "סטינג פגישות מומלץ: ",
      },
      {
        value: "2 לחודש",
        label: "2 לחודש",
        desc: "סטינג פגישות מומלץ: ",
      },
    ],
  },
  {
    label: "לתאם פגישות מעקב:",
    options: [
      {
        value: "פסיכיאטר/ית",
        label: "פסיכיאטר/ית",
        desc: "לתאם פגישות מעקב עם:  ",
      },
      {
        value: "פסיכולוג/ית קליני מומחה",
        label: "פסיכולוג/ית קליני מומחה",
        desc: "לתאם פגישות מעקב עם:  ",
      },
      {
        value: "פסיכולוג/ית התפתחותי מומחה",
        label: "פסיכולוג/ית התפתחותי מומחה",
        desc: "לתאם פגישות מעקב עם:  ",
      },
      {
        value: "פסיכולוג/ית שיקומי מומחה",
        label: "פסיכולוג/ית שיקומי מומחה",
        desc: "לתאם פגישות מעקב עם:  ",
      },
      {
        value: "תומך/ת טיפול",
        label: "תומך/ת טיפול",
        desc: "לתאם פגישות מעקב עם:  ",
      },
    ],
  },
];

export const drugOptions: IOption[] = [
  { value: "Celexa", label: "Celexa" },
  { value: "Prozac", label: "Prozac" },
  { value: "Lexapro", label: "Lexapro" },
  { value: "Luvox", label: "Luvox" },
  { value: "Paxil", label: "Paxil" },
  { value: "Zoloft", label: "Zoloft" },
  { value: "Effexor", label: "Effexor" },
  { value: "Trentellix", label: "Trentellix" },
  { value: "Wellbutrin", label: "Wellbutrin" },
  { value: "Marplan", label: "Marplan" },
  { value: "Remeron", label: "Remeron" },
  { value: "Sertraline", label: "Sertraline" },
  { value: "Dosulepin", label: "Dosulepin" },
  { value: "Duloxetine", label: "Duloxetine" },
  { value: "Escitalopram", label: "Escitalopram " },
  { value: "Lamotrigine", label: "Lamotrigine" },
  { value: "Mirtazapine", label: "Mirtazapine" },
  { value: "Paroxetine", label: "Paroxetine" },
  { value: "Trazodone", label: "Trazodone" },
  { value: "Temazepam", label: "Temazepam" },
  { value: "Lithium", label: "Lithium" },
  { value: "Fluoxetine (prozac)", label: "Fluoxetine (prozac)" },
  { value: "Nocturno", label: "Nocturno" },
];
export const medicineApplicationOptions: IOption[] = [
  { value: "tablet", label: "טבליות TAB" },
  { value: "liquid", label: "נוזל" },
  { value: "caps", label: "כמוסה" },
];
export const medicineDosageOptions: IOption[] = [
  { value: "mg", label: "mg" },
  { value: "ml", label: "ml" },
];
export const auxiliaryWords = [
  "אני",
  t("TRANSFER_APPOINTMENT_NO"),
  "יכול",
  ",",
  " ",
  "",
  "רוצה",
];
export enum ContactUsSearchOptions {
  Input = "input",
  List = "list",
}

// export interface IContactUsTheme {
//   title: string;
//   text: string;
// }

export const contactUsList: IContactUsTheme[] = [
  {
    title: "CONTACTUS_UPGRADE_SUBSCRIPTION_TITLE",
    text: "CONTACTUS_UPGRADE_SUBSCRIPTION_TEXT",
  },
  {
    title: "CONTACTUS_SUBSCRIPTION_TYPES_TITLE",
    text: "CONTACTUS_SUBSCRIPTION_TYPES_TEXT",
  },
  {
    title: "CONTACTUS_GENERAL_INFO_TITLE",
    text: "CONTACTUS_GENERAL_INFO_TEXT",
  },
  {
    title: "CONTACTUS_LOGINPAGE_SIGNUP_TITLE",
    text: "CONTACTUS_LOGINPAGE_SIGNUP_TEXT",
  },
  {
    title: "CONTACTUS_MEETING_WITH_EXPERT_TITLE",
    text: "CONTACTUS_MEETING_WITH_EXPERT_TEXT",
  },
  {
    title: "CONTACTUS_PRESCRIPTIONS_AND_DOCS_TITLE",
    text: "CONTACTUS_PRESCRIPTIONS_AND_DOCS_TEXT",
  },
];

export const techSupportThemes = [
  "MESSAGES",
  "ADD_NAME",
  "GENERAL",
  "PERSONAL_AREA",
  "CHANGE_SETTINGS",
];

export const specializationAreas: ISpecializationArea[] = [
  { id: 1, value: 1, name: "כללי", label: "כללי", nameEn: "general" },
  {
    id: 2,
    value: 2,
    name: "קהל יעד / גילאים",
    label: "קהל יעד / גילאים",
    nameEn: "audience",
  },
  {
    id: 3,
    value: 3,
    name: "גישות טיפוליות / שיטות טיפול",
    label: "גישות טיפוליות / שיטות טיפול",
    nameEn: "approach",
  },
];

export const prescriptionFormInitialState: TPrescriptionForm = {
  medicine: "",
  days: "",
  applicationOptions: "",
  dosage: "",
  dosageValue: "mg",
  numberTimesADay: "",
  instructions: "",
};

export const selectCustomStyles: StylesConfig<IOption, false> = {
  control: (provided) => ({
    ...provided,
    background: "#fff",
    borderColor: "#9e9e9e",
    minHeight: "24px",
    height: "24px",
    marginBottom: "8px",
  }),

  valueContainer: (provided) => ({
    ...provided,
    height: "24px",
    padding: "0 6px",
  }),

  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "24px",
  }),
};

export const noPreferenceLabel = "NO_PREFERENCE";
export const noDataLabel = t("PROFILE_NO_INFO");

export type TLabeledQuestionOption = IOption & TQuestionOption;
export type TGrouppedQuestionsOptions = {
  label: string;
  options: TLabeledQuestionOption[];
};

export const transformServerOptions = (
  optionsArray: TQuestionOption[],
  count: number | null,
  min: number | null
): Array<TGrouppedQuestionsOptions | TLabeledQuestionOption> => {
  const groups: Record<string, TGrouppedQuestionsOptions> = {};
  const noGroup: TLabeledQuestionOption[] = [];

  optionsArray.forEach((option) => {
    const group = option.Groups;

    if (group) {
      // Handle subgroup
      const parentName = group.ParentGroup?.Name ?? null;
      const groupLabel = parentName
        ? `${parentName} › ${group.Name}` // main group > subgroup
        : group.Name;

      const groupKey = `group-${group.Id}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          label: groupLabel,
          options: [],
        };
      }

      groups[groupKey].options.push({
        ...option,
        count,
        min,
        value: option.Id.toString(),
        label: option.Desc,
      });
    } else {
      noGroup.push({
        ...option,
        count,
        min,
        value: option.Id.toString(),
        label: option.Desc,
      });
    }
  });

  // Sort inside each group by Seq
  for (const key in groups) {
    groups[key].options.sort((a, b) => a.Seq - b.Seq);
  }

  noGroup.sort((a, b) => a.Seq - b.Seq);

  // Sort the groups alphabetically or by sequence (optional)
  const groupedArray = Object.values(groups).sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  return [...groupedArray, ...noGroup];
};


// Type guard function for TGrouppedQuestionsOptions
export function isTGrouppedQuestionsOptions(
  object: TGrouppedQuestionsOptions | TLabeledQuestionOption
): object is TGrouppedQuestionsOptions {
  return typeof object === "object" && object !== null && "options" in object;
}

export const findLatestQuestionnaire = (
  id: number,
  questionnaires: TQuestionnaireEntry[]
): TQuestionnaireEntry | undefined => {
  if (questionnaires.length === 0) return undefined;
  const temp = questionnaires.filter((q) => q.QuestionnaireId === id);
  return temp.length
    ? temp
        .filter((q) => q.QuestionnaireId === id)
        .reduce((latest, current) => {
          const latestDate = new Date(latest.Timestamp);
          const currentDate = new Date(current.Timestamp);
          return currentDate > latestDate ? current : latest;
        })
    : undefined;
};

export function calculateAge(birthDateString: string): number {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  
  // Convert to string if it's not already
  const dateStr = String(birthDateString);
  
  // Parse the date string to extract date components, avoiding timezone issues
  // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
  const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return 0;
  }
  
  const birthYear = parseInt(dateMatch[1], 10);
  const birthMonth = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed in JavaScript
  const birthDay = parseInt(dateMatch[3], 10);
  
  // Validate parsed date
  if (isNaN(birthYear) || isNaN(birthMonth) || isNaN(birthDay)) {
    return 0;
  }
  
  let age = todayYear - birthYear;
  
  // Check if the birth month hasn't come yet this year, or if it's the same month but the day hasn't come yet
  if (
    todayMonth < birthMonth ||
    (todayMonth === birthMonth && todayDay < birthDay)
  ) {
    age--;
  }
  
  // Return 0 if age is negative (future birth date)
  return Math.max(0, age);
}

export const RRMListDefault: TRRMListEntry[] = [
  {
    id: 1,
    title: "A",
    desc: null,
    color: "#5d3cc7",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
  {
    id: 2,
    title: "B",
    desc: null,
    color: "#4f0080",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
  {
    id: 3,
    title: "C",
    desc: null,
    color: "#fffe02",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
  {
    id: 4,
    title: "D",
    desc: null,
    color: "#ff7654",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
  {
    id: 5,
    title: "E",
    desc: null,
    color: "#ff7803",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
  {
    id: 6,
    title: "F",
    desc: null,
    color: "#cd2701",
    anliza: "",
    recommendations: "",
    highlights: "",
    conversation: "",
    companyId: 1,
    statusId: 1,
    rrmseq: 1,
    // Min: 0,
    // Max: 0,
  },
];
export const getSelectNote = (
  min: number | null,
  max: number | null
): string => {
  let text = "";
  if (!min) return text;
  if (min) {
    min === 1
      ? max === 1
        ? (text = "SELECT_NOTE_ONE")
        : (text = "SELECT_NOTE_ONE_MIN")
      : (text = `SELECT_NOTE_AT_LEAST`);
  }
  return text;
};
export const getSelectPlaceholder = (
  question: TQuestionnaireQuestion,
  groupId: number | null
): string => {
  const count = question.OptionsCountMax;
  return count === 1
    ? `אפשר לבחור רק אפשרות אחת ${groupId ? "מכל קבוצה" : ""}`
    : `אפשר לבחור עד ${count} אפשרויות`;
};

/**
 * Enforces the per-group selection cap: each group allows at most
 * `min(questionMax, groupMax)` of its own options, so a group never exceeds its
 * own max nor the question's. A missing or 0 max counts as 1, which keeps the legacy
 * "one option from each group" behaviour for questions with OptionsCountMax 1.
 * Ungrouped options are left alone - their cap is the question-level one, which
 * `isOptionDisabled` enforces.
 * The newest picks win, so this doubles as the safety net behind
 * `isOptionDisabled` - selecting past a cap drops the oldest pick in that group.
 */
export function filterLastOfEachGroup(
  options: TLabeledQuestionOption[]
): TLabeledQuestionOption[] {
  if (!options.length) return options;

  // `count` is the question's OptionsCountMax, stamped on every server-built
  // option by transformServerOptions. Synthetic options (ICD code suggestions)
  // don't carry it.
  const questionMax = options.find((o) => o.count != null)?.count ?? null;

  const usedPerGroup: Record<string, number> = {};
  const kept: TLabeledQuestionOption[] = [];

  // Walk newest -> oldest so the latest picks win; original order restored below.
  for (let i = options.length - 1; i >= 0; i--) {
    const item = options[i];

    if (item.GroupId == null) {
      kept.push(item); // ungrouped: capped by isOptionDisabled, not here
      continue;
    }

    const groupCap = Math.min(
      questionMax || 1,
      item.Groups?.OptionsCountMax || 1
    );
    const key = item.GroupId.toString();
    const used = usedPerGroup[key] ?? 0;
    if (used < groupCap) {
      usedPerGroup[key] = used + 1;
      kept.push(item);
    }
  }

  return kept.reverse();
}

// export function findMinAndMax(items: TRRMListEntry[] | null): {
//   min: number;
//   max: number;
// } {
//   if (!items || items.length === 0) {
//     throw new Error("The array is empty");
//   }

//   let min = items[0].Min ?? 0;
//   let max = items[0].Max ?? 1;

//   for (const item of items) {
//     if (item.Min < min) {
//       min = item.Min;
//     }
//     if (item.Max > max) {
//       max = item.Max;
//     }
//   }
//   return { min, max };
// }

export const isFormValid = (form: {
  [key: string]: {
    isValid: boolean;
  };
}): boolean => {
  return Object.keys(form).every((k) => form[k].isValid);
};

export const INACTIVITY_TIMEOUT = 18 * 60 * 1000; // 18 minutes until logout
export const WARNING_TIMEOUT = 15 * 60 * 1000; // 15 minutes until warning
// export const INACTIVITY_TIMEOUT = 4 * 60 * 1000; // for test
// export const WARNING_TIMEOUT = 1 * 60 * 1000; // for test

export const WARNING_DURATION = 3 * 60 * 1000;
export const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

export const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

export const TYPING_CODE = "TYPING_CODE";
export const END_CALL_CODE = "END_CALL_CODE";
export const END_APPOINTMENT = "END_APPOINTMENT";
export const VIDEO_OFF_CODE = "VIDEO_OFF_CODE";
export const VIDEO_ON_CODE = "VIDEO_ON_CODE";

export const chatNoteTypes = {
  isTyping: "isTyping",
  text: "text",
} as const;

export const supportMessageInitialState: TSupportMessage = {
  id: "0",
  companyId: 0,
  screenType: 2,
  subject: "",
  body: "",
};

export const addUserInitialState: TAddUserRequest = {
  id: 0,
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  birth: "",
  genderId: 3,
  passport: "",
  roleId: 1,
  fakeCompanyId: null,
  companyId: 0,
  statusId: 1,
  guid: "",
  treatmentGroupId: 0,
  cityId: null,
  address: "",
};

export const statusOptions: TLabeledItem[] = [
  { value: "1", label: "פעיל" },
  { value: "0", label: "לא פעיל" },
];

export const initialAppointmentsProgram: IAppointmentProgram = {
  id: 0,
  companyId: 0,
  name: "",
  usersGuid: "0",
  roleId: 0,
  specializationsId: 0,
  countOfAppointments: 0,
  appointmentsDurationsId: 0,
  appointmentsDurationsOpenMinutes: null,
  mediasId: 0,
  appointmentsSequenceId: 0,
  appointmentsSequenceOpenCountInPeriod: null,
  createUsersId: 0,
  updateTherapistsUsersId: null,
  updateTherapistsDate: null,
  genderId: 0,
  migzarId: 0,
  comments: "",
  statusId: 1,
  dateToDelete: null, // NotMapped property
  deleteType: null, // NotMapped property
  updateUsersId: null,
  updateDate: null,
  vetek: 0,
  createDate: "",
  updateUsersGuid: null,
  protocolId: null,
  therapistsSpecializationsIds: [],
  fundingSourcesId: null,
  treatmentTypesId: null,
  treatmentRoomId: null,
  treatmentRoomName: null,
  available: false,
};

// Exercise Category Type
export type TExercisesCategory = {
  id: number;
  companyId: number;
  name: string;
  statusId: number;
};

// Exercise Type
export type TExercise = {
  id: number;
  title: string;
  about: string;
  exercisesCategoriesId: number;
  companyId: number;
  generalRRMSEQ: number;
  usersId?: number; // Creator ID
  link?: string;
  statusId: number;
};

// Initial state for new exercise
export const addNewExerciseInitialState: TExercise = {
  id: 0,
  title: "",
  about: "",
  companyId: 0,
  statusId: 1,
  exercisesCategoriesId: 0,
  generalRRMSEQ: 1,
};

export const getStatusOptions = (): TLabeledItem[] => [
  { value: "1", label: t("STATUS_ACTIVE") },
  { value: "0", label: t("STATUS_INACTIVE") },
];

export const roundPrice = (value: number): number => {
  return Math.round(value);
};

export const DEFAULT_LANGUAGE = 'he';


// Table IDs from database
export const TABLE_IDS = {
  NEW_INQUIRIES: 1,
  MY_INQUIRIES: 2,
  TREATMENT_PLANS: 3,
} as const;

// Permission types from database
export const PERMISSION_TYPES = {
  NO_PERMISSION: 1,
  READ_ONLY: 2,
  EDIT: 3,
} as const;